/**
 * Product Service
 *
 * Handles all product-related API operations including:
 * - Listing products
 * - Searching products
 * - Filtering products
 * - Comparing products
 * - Retrieving product details
 */

import { baseClient } from './base-client';
import type {
  ApiResponse,
  Product,
  GetProductsRequest,
  GetProductsResponse,
  SearchProductsRequest,
  SearchProductsResponse,
  FilterProductsRequest,
  CompareProductsRequest,
  CompareProductsResponse,
} from './types';

class ProductService {
  private readonly basePath = '/products';

  /**
   * Get a paginated list of products
   */
  async getProducts(request: GetProductsRequest): Promise<ApiResponse<GetProductsResponse>> {
    const params = {
      limit: request.limit,
      page: request.page,
      ...request.filters,
    };

    return baseClient.get<GetProductsResponse>(this.basePath, { params });
  }

  /**
   * Get details for a specific product
   */
  async getProductDetails(productId: string): Promise<ApiResponse<Product>> {
    return baseClient.get<Product>(`${this.basePath}/${productId}`);
  }

  /**
   * Search for products by query string
   */
  async searchProducts(request: SearchProductsRequest): Promise<ApiResponse<SearchProductsResponse>> {
    const params = {
      query: request.query,
      limit: request.limit,
    };

    return baseClient.get<SearchProductsResponse>(`${this.basePath}/search`, { params });
  }

  /**
   * Filter products by criteria
   */
  async filterProducts(request: FilterProductsRequest): Promise<ApiResponse<GetProductsResponse>> {
    return baseClient.post<GetProductsResponse>(`${this.basePath}/filter`, request);
  }

  /**
   * Compare multiple products side-by-side
   */
  async compareProducts(request: CompareProductsRequest): Promise<ApiResponse<CompareProductsResponse>> {
    return baseClient.post<CompareProductsResponse>(`${this.basePath}/compare`, request);
  }

  /**
   * Get products by IDs
   */
  async getProductsByIds(productIds: string[]): Promise<ApiResponse<Product[]>> {
    return baseClient.post<Product[]>(`${this.basePath}/batch`, { productIds });
  }
}

/**
 * Export singleton instance
 */
export const productService = new ProductService();
